import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export function useNewsletterForm(onSuccess: (email?: string, isAlreadySubscribed?: boolean) => void) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [acceptNewsletter, setAcceptNewsletter] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const checkExistingSubscriber = async (email: string): Promise<{ exists: boolean; isVerified: boolean; id?: string }> => {
    console.log("🔍 checkExistingSubscriber - Starting to check for email:", email);
    
    const normalizedEmail = email.toLowerCase().trim();
    console.log("🔍 checkExistingSubscriber - Normalized email:", normalizedEmail);
    
    try {
      console.log("🔍 checkExistingSubscriber - Running secure function for:", normalizedEmail);
      
      const { data, error } = await supabase
        .rpc('check_newsletter_email_exists', {
          check_email: normalizedEmail
        })
        .single();
      
      console.log("🔍 checkExistingSubscriber - Function result:", { data, error });
      
      if (error) {
        console.error("❌ checkExistingSubscriber - Error checking for existing subscriber:", error);
        return { exists: false, isVerified: false };
      }
      
      if (!data || !data.email_exists) {
        console.log("🔍 checkExistingSubscriber - No existing subscriber found");
        return { exists: false, isVerified: false };
      }
      
      const result = { 
        exists: true, 
        isVerified: data.is_verified && data.is_active
      };
      console.log("🔍 checkExistingSubscriber - Subscriber exists:", result);
      return result;
    } catch (error) {
      console.error("❌ checkExistingSubscriber - Exception checking for existing subscriber:", error);
      return { exists: false, isVerified: false };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log("🚀 handleSubmit - Starting form submission");
    e.preventDefault();
    
    setEmailError("");
    
    if (!email || !firstName || !lastName) {
      console.log("❌ handleSubmit - Missing required fields");
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (!validateEmail(email)) {
      console.log("❌ handleSubmit - Invalid email format:", email);
      setEmailError("Veuillez entrer une adresse email valide");
      toast.error("L'adresse email n'est pas valide");
      return;
    }

    if (!acceptNewsletter) {
      console.log("❌ handleSubmit - Newsletter terms not accepted");
      toast.error("Veuillez accepter les conditions pour vous inscrire à la newsletter");
      return;
    }

    setIsSubmitting(true);
    console.log("🔄 handleSubmit - Form is valid, checking if subscriber exists");
    
    try {
      const normalizedEmail = email.toLowerCase().trim();
      console.log("🔄 handleSubmit - Checking for normalized email:", normalizedEmail);
      
      const { exists, isVerified } = await checkExistingSubscriber(normalizedEmail);
      console.log("🔄 handleSubmit - Existing subscriber check result:", { exists, isVerified });
      
      if (exists) {
        console.log("🔄 handleSubmit - Subscriber exists, checking verification status");
        if (isVerified) {
          console.log("✅ handleSubmit - Subscriber is already verified");
          toast.info("Vous êtes déjà inscrit à notre newsletter avec cette adresse email");
        } else {
          console.log("⚠️ handleSubmit - Subscriber exists but not verified yet");
          toast.info("Un email de vérification a déjà été envoyé à cette adresse. Veuillez vérifier votre boîte de réception et cliquer sur le lien de confirmation.");
        }
        
        setEmail("");
        setFirstName("");
        setLastName("");
        setAcceptNewsletter(false);
        
        if (onSuccess) {
          console.log("✅ handleSubmit - Calling onSuccess callback for existing subscriber");
          onSuccess(normalizedEmail, true);
        }
        
        setIsSubmitting(false);
        return;
      }
      
      console.log("🔄 handleSubmit - New subscriber, submitting form data:", { email: normalizedEmail, firstName, lastName });
      
      const { data, error } = await supabase.functions.invoke('add-newsletter-subscriber', {
        body: {
          email: normalizedEmail,
          firstName,
          lastName,
          skipVerification: false
        }
      });
      
      console.log("🔄 handleSubmit - Response from add-newsletter-subscriber:", { data, error });
      
      if (error) {
        console.error("❌ handleSubmit - Error submitting newsletter form:", error);
        toast.error("Une erreur est survenue lors de l'inscription");
        setIsSubmitting(false);
        return;
      }
      
      if (data && data.alreadySubscribed) {
        console.log("✅ handleSubmit - Server reports subscriber already exists and is verified");
        toast.success(data.message || "Vous êtes déjà inscrit à notre newsletter");
        
        setEmail("");
        setFirstName("");
        setLastName("");
        setAcceptNewsletter(false);
        
        if (onSuccess) {
          console.log("✅ handleSubmit - Calling onSuccess callback for server-reported already subscribed");
          onSuccess(normalizedEmail, true);
        }
        
        setIsSubmitting(false);
        return;
      }
      
      console.log("✅ handleSubmit - Verification email sent successfully");
      toast.success(data.message || "Un email de confirmation a été envoyé à votre adresse");
      
      setEmail("");
      setFirstName("");
      setLastName("");
      setAcceptNewsletter(false);
      
      if (onSuccess) {
        console.log("✅ handleSubmit - Calling onSuccess callback after successful submission");
        onSuccess(normalizedEmail, false);
      }
      
    } catch (error: any) {
      console.error("❌ handleSubmit - Exception submitting newsletter form:", error);
      toast.error(error.message || "Une erreur est survenue lors de l'inscription");
    } finally {
      console.log("🏁 handleSubmit - Submission process complete");
      setIsSubmitting(false);
    }
  };

  return {
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    acceptNewsletter,
    setAcceptNewsletter,
    isSubmitting,
    emailError,
    setEmailError,
    handleSubmit
  };
}
